import * as React from 'react';
import type { ClusterKind, RateSnapshotData } from './types';
import type { RoomIsland, ProgramIsland } from './ClusterAssignmentPanel';
import { CreateClusterTotalPriceCell } from './CreateClusterTotalPriceCell';

interface Props {
    roomIslands: RoomIsland[];
    programIslands: ProgramIsland[];
    clusterAllDates: Map<string, string[]>;
    clusterSelections: Map<string, Set<string>>;
    editingClusterId: string | null;
    onEditCluster: (clusterId: string | null) => void;
    targetCurrent: RateSnapshotData | null;
}

export function CreateClusterListPanel({
    roomIslands, programIslands, clusterAllDates, clusterSelections,
    editingClusterId, onEditCluster, targetCurrent,
}: Props) {
    return (
        <div className="flex flex-col gap-3">
            {roomIslands.length > 0 && (
                <Group
                    title="Rooms"
                    kind="ROOM"
                    rows={roomIslands.map(i => ({
                        id: i.id,
                        displayName: i.displayName,
                        dateRange: i.dateRange,
                        segmentCount: i.chain.length,
                        memberIds: i.chain.map(c => c.reservationRoomId),
                    }))}
                    clusterAllDates={clusterAllDates}
                    clusterSelections={clusterSelections}
                    editingClusterId={editingClusterId}
                    onEditCluster={onEditCluster}
                    targetCurrent={targetCurrent}
                />
            )}
            {programIslands.length > 0 && (
                <Group
                    title="Programs"
                    kind="PROGRAM"
                    rows={programIslands.map(i => ({
                        id: i.id,
                        displayName: i.displayName,
                        dateRange: i.dateRange,
                        segmentCount: i.chain.length,
                        memberIds: i.chain.map(c => c.reservationProgramId),
                    }))}
                    clusterAllDates={clusterAllDates}
                    clusterSelections={clusterSelections}
                    editingClusterId={editingClusterId}
                    onEditCluster={onEditCluster}
                    targetCurrent={targetCurrent}
                />
            )}
        </div>
    );
}

interface ClusterRow {
    id: string;
    displayName: string;
    dateRange: string;
    segmentCount: number;
    memberIds: string[];
}

interface GroupProps {
    title: string;
    kind: ClusterKind;
    rows: ClusterRow[];
    clusterAllDates: Map<string, string[]>;
    clusterSelections: Map<string, Set<string>>;
    editingClusterId: string | null;
    onEditCluster: (clusterId: string | null) => void;
    targetCurrent: RateSnapshotData | null;
}

function Group({
    title, kind, rows, clusterAllDates, clusterSelections, editingClusterId, onEditCluster, targetCurrent,
}: GroupProps) {
    return (
        <div>
            <h6 className="font-semibold mb-2">{title}</h6>
            {rows.map(row => {
                const totalDates = clusterAllDates.get(row.id)?.length ?? 0;
                const selectedDates = clusterSelections.get(row.id) ?? new Set<string>();
                const selectedCount = selectedDates.size;
                const isEditing = editingClusterId === row.id;
                const allSelected = totalDates > 0 && selectedCount === totalDates;
                const noneSelected = selectedCount === 0;

                return (
                    <div
                        key={row.id}
                        className={`border rounded p-3 mb-2 ${isEditing ? 'border-primary' : ''}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                                <div>
                                    <span className="font-medium text-sm">{row.displayName}</span>
                                    {row.segmentCount > 1 && (
                                        <span className="badge bg-info text-xs ms-2">{row.segmentCount} segments</span>
                                    )}
                                    <span className="badge bg-secondary text-xs ms-2">{kind === 'ROOM' ? 'Room' : 'Program'}</span>
                                </div>
                                <div className="text-xs text-gray-500">{row.dateRange}</div>
                                <div className={`text-xs mt-1 mb-2 ${noneSelected ? 'text-danger' : 'text-gray-600'}`}>
                                    {allSelected
                                        ? `All ${totalDates} dates selected`
                                        : noneSelected
                                            ? 'No dates selected — cluster will be skipped'
                                            : `${selectedCount} of ${totalDates} dates selected`}
                                </div>
                                <CreateClusterTotalPriceCell
                                    cluster={{
                                        id: row.id,
                                        kind,
                                        memberIds: row.memberIds,
                                        displayName: row.displayName,
                                    }}
                                    targetCurrent={targetCurrent}
                                    selectedDates={selectedDates}
                                />
                            </div>
                            <button
                                type="button"
                                className={`btn btn-sm ${isEditing ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => onEditCluster(isEditing ? null : row.id)}
                            >
                                {isEditing ? 'Editing' : 'Edit'}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
